import { Data, EntityType } from 'common/enums';
import { ApiInitiator } from 'common/enums/data.enum';
import { EntityVM } from 'common/models/data/entity.model';
import { ConfigurationIndicatorVM } from 'modules/configuration/models/configuration.model';

export const convertTreeToFlatData = (data: any, level: number, catId: number) => {
    const newData = JSON.parse(JSON.stringify(data));
    return newData.reduce((final: any, item: any) => {
        const newCatId = item.entityType === EntityType.Category ? item.id : catId;
        if (!item.isExpanded) {
            if (item.hasOwnProperty(Data.SubCategories) && item.subCategories.length > 0) {
                item.hasSubCategories = true;
            }
            delete item[Data.SubCategories];
            item.level = level;
            item.catId = newCatId;
            item.categoryId = newCatId;
            final.push(item);
        } else {
            const newItem = Object.assign({}, item);
            if (newItem.hasOwnProperty(Data.SubCategories) && newItem.subCategories.length > 0) {
                newItem.hasSubCategories = true;
            }
            delete newItem[Data.SubCategories];
            newItem.level = level;
            const catId = newCatId;
            newItem.catId = catId;
            newItem.categoryId = catId;
            final.push(newItem);
            item.subCategories &&
                final.push(...convertTreeToFlatData(item.subCategories, level + 1, newCatId));
        }
        return final;
    }, []);
};

export const addCategories = (data: any, targetId: string, newData: any) => {
    return data.map((item: any) => {
        if (item.id === targetId) {
            if (item[Data.SubCategories] && item[Data.SubCategories].length > 0) {
                if (Array.isArray(newData)) {
                    item[Data.SubCategories].push([...newData]);
                } else {
                    item[Data.SubCategories].push(newData);
                }
            } else {
                if (Array.isArray(newData)) {
                    item[Data.SubCategories] = newData;
                } else {
                    item[Data.SubCategories] = [newData];
                }
            }
        } else {
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                item.subCategories = addCategories(item.subCategories, targetId, newData);
            }
        }
        return item;
    });
};

export const addCategoriesByHierarchyId = (data: any, targetId: string, newData: any) => {
    return data.map((item: any) => {
        if (item.hierarchyId === targetId) {
            if (item[Data.SubCategories] && item[Data.SubCategories].length > 0) {
                if (Array.isArray(newData)) {
                    item[Data.SubCategories].push([...newData]);
                } else {
                    item[Data.SubCategories].push(newData);
                }
            } else {
                if (Array.isArray(newData)) {
                    item[Data.SubCategories] = newData;
                } else {
                    item[Data.SubCategories] = [newData];
                }
            }
        } else {
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                item.subCategories = addCategoriesByHierarchyId(
                    item.subCategories,
                    targetId,
                    newData
                );
            }
        }
        return item;
    });
};

export const findCategories = (data: any, categoryId: string, matchedData: any = {}) => {
    const newData = JSON.parse(JSON.stringify(data));
    newData.forEach((item: any) => {
        if (item.id.toString() === categoryId) {
            matchedData = item;
        } else {
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                matchedData = findCategories(item.subCategories, categoryId, matchedData);
            }
        }
    });
    return matchedData;
};

export const findCategoriesByHierarchyId = (
    data: any,
    hierarchyId: string,
    matchedData: any = {}
) => {
    const newData = JSON.parse(JSON.stringify(data));
    newData.forEach((item: any) => {
        if (item.hierarchyId && item.hierarchyId.toString() === hierarchyId) {
            matchedData = item;
        } else {
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                matchedData = findCategoriesByHierarchyId(
                    item.subCategories,
                    hierarchyId,
                    matchedData
                );
            }
        }
    });
    return matchedData;
};

export const removeCategories = (data: any, categoryId: string) => {
    return data.filter((item: any) => {
        if (Data.SubCategories in item) {
            if (
                item.subCategories.findIndex((sc: any) => sc.id === categoryId) > -1 &&
                item.subCategories.length === 1
            ) {
                item.hasChild = false;
            }
            item.subCategories = removeCategories(item.subCategories, categoryId);
        }
        return item.id.toString() !== categoryId;
    });
};

export const toggleExpansion = (toggledData: any, data: any, initiator: ApiInitiator) => {
    if (initiator === ApiInitiator.MasterData)
        return data.map((item: any) => {
            if (toggledData.entityType === EntityType.Category && item.id === toggledData.id) {
                const newExpandedState = !item.isExpanded;
                item.isExpanded = newExpandedState;
                if (item.subCategories) {
                    item.subCategories = item.subCategories.map((subCat: any) => {
                        return {
                            ...subCat,
                            isParentExpanded: newExpandedState
                        };
                    });
                }
            } else if (
                toggledData.entityType !== EntityType.Category &&
                item.hierarchyId === toggledData.hierarchyId
            ) {
                const newExpandedState = !item.isExpanded;
                item.isExpanded = newExpandedState;
                if (item.subCategories) {
                    item.subCategories = item.subCategories.map((subCat: any) => {
                        return {
                            ...subCat,
                            isParentExpanded: newExpandedState
                        };
                    });
                }
            } else {
                if (
                    item.subCategories &&
                    Array.isArray(item.subCategories) &&
                    item.subCategories.length > 0
                ) {
                    item.subCategories = toggleExpansion(
                        toggledData,
                        item.subCategories,
                        initiator
                    );
                }
            }
            return item;
        });
    else {
        return data.map((item: any) => {
            if (item.id === toggledData.id) {
                const newExpandedState = !item.isExpanded;
                item.isExpanded = newExpandedState;
                if (item.subCategories) {
                    item.subCategories = item.subCategories.map((subCat: any) => {
                        return {
                            ...subCat,
                            isParentExpanded: newExpandedState
                        };
                    });
                }
            } else {
                if (
                    item.subCategories &&
                    Array.isArray(item.subCategories) &&
                    item.subCategories.length > 0
                ) {
                    item.subCategories = toggleExpansion(
                        toggledData,
                        item.subCategories,
                        initiator
                    );
                }
            }
            return item;
        });
    }
};

export const updateCategory = (data: any, targetId: string, newData: any) => {
    return data.map((item: any) => {
        if (item.id === targetId) {
            item = { ...item, ...newData };
        } else {
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                item.subCategories = updateCategory(item.subCategories, targetId, newData);
            }
        }
        return item;
    });
};

export const updateSubCategoryByHierarchyId = (data: any, targetId: string, newData: any) => {
    return data.map((item: any) => {
        if (item.hierarchyId === targetId) {
            item = { ...item, ...newData };
        } else {
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                item.subCategories = updateSubCategoryByHierarchyId(
                    item.subCategories,
                    targetId,
                    newData
                );
            }
        }
        return item;
    });
};

export const updateErrorFixedStatus = (data: any, catId: any) => {
    return data.map((item: any) => {
        if (item.id === catId) {
            item.isErrorFixed = true;
            item.hasError = false;
        }
        item.subCategories =
            item.subCategories && updateErrorFixedStatus(item.subCategories, catId);
        return item;
    });
};

export const updateParentErrorFixedStatus = (data: any, parentId: string) => {
    const entity = findCategories(data, parentId);
    if (entity.subCategories && entity.subCategories.length > 0) {
        if (
            entity.childHasError &&
            entity.subCategories.filter((sc: any) => sc.hasError || sc.childHasError).length === 0
        ) {
            entity.childHasError = false;
            if (!entity.hasError) {
                entity.isErrorFixed = true;
            }
            data = updateCategory(data, entity.id, entity);
            if (!entity.hasError && entity.parentId && entity.parentId.trim().length > 0) {
                data = updateParentErrorFixedStatus(data, entity.parentId);
            }
        }
    }
    return data;
};

export const updateParentErrorFixedStatusOnDelete = (data: any, parentId: string) => {
    const entity = findCategories(data, parentId);
    if (
        (entity.subCategories && entity.subCategories.length > 0) ||
        (entity.subCategories && entity.subCategories.length === 0)
    ) {
        if (
            entity.childHasError &&
            entity.subCategories.filter((sc: any) => sc.hasError || sc.childHasError).length === 0
        ) {
            entity.childHasError = false;
            if (!entity.hasError && entity.subCategories && entity.subCategories.length > 0) {
                entity.isErrorFixed = true;
            }
            if (entity.subCategories && entity.subCategories.length === 0) {
                entity.isExpanded = false;
            }
            data = updateCategory(data, entity.id, entity);
            if (!entity.hasError && entity.parentId && entity.parentId.trim().length > 0) {
                data = updateParentErrorFixedStatus(data, entity.parentId);
            }
        }
    }
    return data;
};

const updateChildErrorFixedStatus = (data: any) => {
    return data.map((item: any) => {
        if (item.entityType !== EntityType.Indicator) {
            item.isErrorFixed = true;
        }
        if (
            item.subCategories &&
            Array.isArray(item.subCategories) &&
            item.subCategories.length > 0
        ) {
            item.subCategories = updateChildErrorFixedStatus(item.subCategories);
        }
        return item;
    });
};

export const checkIfItHasError = (data: any) => {
    let flag = false;

    for (let i = 0; i < data.length; i++) {
        if (data[i].childHasError || data[i].hasError) {
            flag = true;
            break;
        }
    }
    return flag;
};

export const checkIfCategoryHasSubCategoryOrIndicator = (data: any) => {
    const err = Array<string>();
    data.map((item: any) => {
        if (item.subCategories.length === 0) err.push(item.name);
    });
    return err;
};

/**
 * Following function is written to choose a record by selecting radio button from "Add subcategory" and "Move indicator/subcategory"
 * @param data is array of master/preview data
 * @param targetId Record id which need to select
 * @returns
 */
export const setRadioButton = (data: any, targetId: string, field = 'id') => {
    data.map((item: any) => {
        if (item[field] === targetId) {
            item.isSelected = true;
        } else {
            item.isSelected = false;
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                item.subCategories = setRadioButton(item.subCategories, targetId, field);
            }
        }
    });
    return data;
};

/**
 * Following function is written to de select selecting radio button from "Add subcategory" and "Move indicator/subcategory"
 * @param data is array of master/preview data
 * @param targetId Record id which need to select
 * @returns
 */
export const deselectRadioButton = (data: any) => {
    data.map((item: any) => {
        item.isSelected = false;
        if (
            item.subCategories &&
            Array.isArray(item.subCategories) &&
            item.subCategories.length > 0
        ) {
            item.subCategories = deselectRadioButton(item.subCategories);
        }
    });
    return data;
};

/**
 * Following function is written for select and deselect check box from "Map to variant" page
 * @param data is array of master/preview data
 * @param targetId Record id which need to select
 * @returns
 */
export const setCheckbox = (data: any, targetId: string | number) => {
    data.map((item: any) => {
        if (item.id === targetId) {
            item.isSelected = !item.isSelected;
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                item.subCategories = setSelectAllCheckbox(item.subCategories, item.isSelected);
            }
        } else {
            if (
                item.subCategories &&
                Array.isArray(item.subCategories) &&
                item.subCategories.length > 0
            ) {
                item.subCategories = setCheckbox(item.subCategories, targetId);
            }
        }
    });
    return data;
};

/**
 * Following function is written for select all and deselect all check box from "Map to variant" page
 * @param data is array of master/preview data
 * @param isSelected is boolean value (true/false) of select all check box
 * @returns
 */
export const setSelectAllCheckbox = (data: any, isSelected: boolean) => {
    data.map((item: any) => {
        item.isSelected = isSelected;
        if (
            item.subCategories &&
            Array.isArray(item.subCategories) &&
            item.subCategories.length > 0
        ) {
            item.subCategories = setSelectAllCheckbox(item.subCategories, isSelected);
        }
    });
    return data;
};

export const removeByEntityType = (data: any, entityType: string) => {
    return data
        .filter((item: any) => item.entityType !== entityType)
        .map((item: any) => {
            if (item[Data.SubCategories] && item[Data.SubCategories].length > 0) {
                item = {
                    ...item,
                    subCategories: item.subCategories.filter(
                        (el: any) => el.entityType !== entityType
                    )
                };
                if (item[Data.SubCategories].length > 0) {
                    item = {
                        ...item,
                        subCategories: removeByEntityType(item.subCategories, entityType)
                    };
                }
            }
            return item;
        });
};

/**
 * The function "mapIndicators" marks indicators as checked which are already selected in
 * the configuration list. It takes an array of configurationList of type
 * ConfigurationIndicatorVM[] and expanded subCatData of type EntityVM[].
 * ConfigurationIndicatorVM objects, and returns the subCatData array.
 * @param {EntityVM[]} subCatData - An array of EntityVM objects representing sub-category data.
 * @param {ConfigurationIndicatorVM[]} configurationList - The `configurationList` parameter is an
 * array of `ConfigurationIndicatorVM` objects.
 * @returns the modified subCategoryData array (indicator marked as isSelected=true if it is in
 * configuration list) .
 */
export const mapIndicators = function (
    subCatData: EntityVM[],
    configurationList: ConfigurationIndicatorVM[]
): EntityVM[] {
    return subCatData.map((subCat: EntityVM) => {
        if (subCat.entityType === EntityType.Indicator) {
            const configIndicator = configurationList.find((config: ConfigurationIndicatorVM) => {
                return config.indicatorId === subCat.id;
            });
            if (configIndicator) {
                subCat.isSelected = true;
            }
        }
        return subCat;
    });
};

/**
 * Following function is used to collapse hierarchical data
 * @param data is array of master/preview data
 * @returns collapsed hierarchical data
 */
export const collapseHierarchicalData = (data: any) => {
    data.map((item: any) => {
        item.isExpanded = false;
        if (
            item.subCategories &&
            Array.isArray(item.subCategories) &&
            item.subCategories.length > 0
        ) {
            item.subCategories = collapseHierarchicalData(item.subCategories);
        }
    });
    return data;
};
